import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';

const ClientRegistration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  const validateToken = useCallback(async () => {
    try {
      const response = await api.post('/client-registration/decrypt-token', { token });
      setValidToken(true);
      setClientInfo(response.data);
    } catch (error) {
      setError('Invalid or expired registration link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const checkUsernameAvailability = useCallback(async () => {
    setCheckingUsername(true);
    try {
      const response = await api.post('/client-registration/check-username', { 
        username: formData.username 
      });
      setUsernameAvailable(response.data.available);
    } catch (error) {
      setUsernameAvailable(false);
    } finally {
      setCheckingUsername(false);
    }
  }, [formData.username]);

  useEffect(() => {
    if (!token) {
      setError('Invalid registration link');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token, validateToken]);

  useEffect(() => {
    if (formData.username.length >= 3) {
      checkUsernameAvailability();
    } else {
      setUsernameAvailable(null);
    }
  }, [formData.username, checkUsernameAvailability]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (usernameAvailable === false) {
      setError('Username is not available');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post('/client-registration/complete', {
        token,
        username: formData.username,
        password: formData.password
      });

      // Store token and user info
      localStorage.setItem('clientToken', response.data.token);
      localStorage.setItem('clientUser', JSON.stringify(response.data.user));

      // Redirect to main portal
      navigate('/');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f3f4f6'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <p>Validating registration link...</p>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f3f4f6'
      }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '40px', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', color: '#ef4444' }}>❌</div>
          <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>Invalid Registration Link</h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={() => navigate('/client-login')}
            style={{
              backgroundColor: '#1e3a8a',
              color: 'white',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: '#f3f4f6'
    }}>
      <div style={{ 
        backgroundColor: 'white', 
        padding: '40px', 
        borderRadius: '12px', 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ color: '#1e3a8a', marginBottom: '8px' }}>Complete Registration</h1>
          <p style={{ color: '#666', margin: 0 }}>
            Welcome to Optitrac Client Portal
          </p>
        </div>

        {clientInfo && (
          <div style={{ 
            backgroundColor: '#f0f9ff', 
            padding: '16px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            border: '1px solid #0ea5e9'
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>{clientInfo.clientName}</p>
            <p style={{ margin: '0 0 8px 0', color: '#666' }}>{clientInfo.companyName}</p>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{clientInfo.email}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500',
              color: '#374151'
            }}>
              Username *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${
                  usernameAvailable === false ? '#ef4444' : 
                  usernameAvailable === true ? '#10b981' : '#d1d5db'
                }`,
                borderRadius: '6px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="Choose a username"
              required
            />
            {formData.username.length >= 3 && (
              <div style={{ marginTop: '4px', fontSize: '14px' }}>
                {checkingUsername ? (
                  <span style={{ color: '#6b7280' }}>Checking availability...</span>
                ) : usernameAvailable === true ? (
                  <span style={{ color: '#10b981' }}>✓ Username available</span>
                ) : usernameAvailable === false ? (
                  <span style={{ color: '#ef4444' }}>✗ Username not available</span>
                ) : null}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500',
              color: '#374151'
            }}>
              Password *
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="Choose a password (min 6 characters)"
              required
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500',
              color: '#374151'
            }}>
              Confirm Password *
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              placeholder="Confirm your password"
              required
            />
          </div>

          {error && (
            <div style={{ 
              backgroundColor: '#fef2f2', 
              color: '#dc2626', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '20px',
              border: '1px solid #fecaca'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || usernameAvailable === false}
            style={{
              width: '100%',
              backgroundColor: submitting || usernameAvailable === false ? '#9ca3af' : '#1e3a8a',
              color: 'white',
              padding: '12px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: submitting || usernameAvailable === false ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Creating Account...' : 'Complete Registration'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <p style={{ color: '#666', fontSize: '14px' }}>
            Already have an account?{' '}
            <button
              onClick={() => navigate('/client-login')}
              style={{
                background: 'none',
                border: 'none',
                color: '#1e3a8a',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientRegistration;
